import { useEffect, useRef } from 'react'
import { setWaitingCount } from '@/features/storefront-chat/alerts-store'
import { useStorefrontChatEvents, useStorefrontChatSummary } from '@/features/storefront-chat/hooks'
import { setAppBadge, useAlertNavigation, useIsChatResponder } from '@/features/storefront-chat/phone-alerts-device'

/** A short two-note chime; browsers stay silent until the page has been interacted with. */
function playChime() {
  const AudioContextClass =
    window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
  if (!AudioContextClass) return
  try {
    const ctx = new AudioContextClass()
    const notes = [880, 1318.5]
    notes.forEach((frequency, i) => {
      const start = ctx.currentTime + i * 0.16
      const osc = ctx.createOscillator()
      const gain = ctx.createGain()
      osc.type = 'sine'
      osc.frequency.value = frequency
      gain.gain.setValueAtTime(0.0001, start)
      gain.gain.exponentialRampToValueAtTime(0.18, start + 0.02)
      gain.gain.exponentialRampToValueAtTime(0.0001, start + 0.35)
      osc.connect(gain).connect(ctx.destination)
      osc.start(start)
      osc.stop(start + 0.4)
    })
    window.setTimeout(() => void ctx.close(), 1000)
  } catch {
    // No audio device or blocked autoplay: the badge still shows.
  }
}

/**
 * Mounted once for the signed-in dashboard: follows every chat live and opens the chat an alert
 * was tapped for. For store chat responders (USA-1483) it also keeps the sidebar badge and the
 * app icon number current, and chimes when another chat starts waiting on a person's reply.
 */
export function StorefrontChatAlertsMount() {
  useStorefrontChatEvents()
  useAlertNavigation()
  const responder = useIsChatResponder()
  const { data } = useStorefrontChatSummary(responder)
  const previous = useRef<number | null>(null)
  const count = responder ? data?.awaiting_reply : 0

  useEffect(() => {
    if (count == null) return
    setWaitingCount(count)
    setAppBadge(count)
    if (previous.current != null && count > previous.current) playChime()
    previous.current = count
  }, [count])

  return null
}
